// =====================================================
// TDI COACHING SECTION - AI-Powered Discipline Coaching
// =====================================================

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import { FiMessageCircle, FiRefreshCw, FiChevronDown, FiChevronUp, FiZap } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

interface TDICoachingSectionProps {
  tdiData: {
    tdi_score?: number;
    btdi?: number;
    cetdi?: number;
    components?: {
      behavior?: {
        capital_preservation_behavior?: number;
        decision_consistency?: number;
        risk_avoidance?: number;
        behavioral_stability?: number;
      };
      capital?: {
        capital_preservation_score?: number;
        growth_score?: number;
        good_skips_usd?: number;
        bad_skips_usd?: number;
      };
    };
    directional_bias?: {
      has_warning?: boolean;
      dominant_side?: string;
      long_winrate?: number;
      short_winrate?: number;
    };
    stats?: {
      decisions_count?: number;
      executed_trades_count?: number;
      has_pnl_data?: boolean;
      has_counterfactual_data?: boolean;
    };
    period_days?: number;
  } | null;
}

const TDICoachingSection: React.FC<TDICoachingSectionProps> = ({ tdiData }) => {
  const [coaching, setCoaching] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const generateCoaching = async () => {
    if (!tdiData || !tdiData.tdi_score) {
      setError('Need at least 5 decisions to generate coaching');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Format data for the coaching engine with full stats
      const formattedData = {
        tdi_final_scaled: tdiData.tdi_score,
        btdi: tdiData.btdi || 0,
        cetdi: tdiData.cetdi || 0,
        components: {
          behavior: tdiData.components?.behavior || {},
          capital: tdiData.components?.capital || {}
        },
        directional_bias: tdiData.directional_bias || { has_warning: false },
        stats: {
          decisions_count: tdiData.stats?.decisions_count || 0,
          executed_trades_count: tdiData.stats?.executed_trades_count || 0,
          has_pnl_data: tdiData.stats?.has_pnl_data || false,
          has_counterfactual_data: tdiData.stats?.has_counterfactual_data || false
        },
        meta: {
          period_label: tdiData.period_days ? `Last ${tdiData.period_days} days` : 'All time',
          trader_profile: 'discretionary'
        }
      };

      const { data, error: fnError } = await supabase.functions.invoke('tdi-coaching', {
        body: { tdiData: formattedData }
      });

      if (fnError) {
        throw new Error(fnError.message);
      }

      if (data?.coaching) {
        setCoaching(data.coaching);
      } else {
        throw new Error('No coaching response received');
      }
    } catch (err) {
      console.error('Error generating coaching:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate coaching');
    } finally {
      setIsLoading(false);
    }
  };

  if (!tdiData) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-5 rounded-xl bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/30"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-purple-600/30 to-cyan-600/30 flex items-center justify-center">
            <SafeIcon icon={FiMessageCircle} className="w-4 h-4 text-cyan-400" />
          </div>
          <div>
            <h4 className="text-sm font-semibold text-white">AI Discipline Coach</h4>
            <p className="text-xs text-gray-500">Personalized TDI insights</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={generateCoaching}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 text-cyan-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-purple-500/20 text-sm font-medium"
          >
            {isLoading ? (
              <>
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4 animate-spin" />
                Analyzing...
              </>
            ) : coaching ? (
              <>
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4" />
                Refresh
              </>
            ) : (
              <>
                <SafeIcon icon={FiZap} className="w-4 h-4" />
                Get Coaching
              </>
            )}
          </button>
          {coaching && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1.5 rounded-lg hover:bg-white/5 transition-colors"
            >
              <SafeIcon 
                icon={isExpanded ? FiChevronUp : FiChevronDown} 
                className="w-4 h-4 text-gray-400" 
              />
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm mb-4">
          {error}
        </div>
      )}

      {/* Coaching Content */}
      <AnimatePresence>
        {coaching && isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="prose prose-invert prose-sm max-w-none"
          >
            <div className="p-4 rounded-lg bg-gray-800/50 border border-white/5 text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
              {coaching}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Placeholder when no coaching yet */}
      {!coaching && !error && !isLoading && (
        <div className="p-4 rounded-lg bg-gray-800/30 border border-white/5 text-center">
          <p className="text-gray-500 text-sm">
            Click "Get Coaching" to receive personalized discipline insights based on your TDI score.
          </p>
        </div>
      )}
    </motion.div>
  );
};

export default TDICoachingSection;
